import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AVAILABILITY_OPTIONS, LANGUAGE_OPTIONS, SUBJECTS_TREE } from "../lib/constants";
import { compressImageToMaxSize } from "../lib/imageCompress";

export default function CreateProfile() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const presetType = searchParams.get("type"); // "canguro" | "profesor" | null
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState({});
  const [existingId, setExistingId] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "", type: presetType === "profesor" ? "profesor" : "canguro", bio: "", price_per_hour: 12, photo_url: "",
    comunidad: "", ciudad: "", zonas: [],
    availability: [], languages: [], subjects: {},
    accepts_privacy_policy: false, accepts_communications: false,
  });

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data.locations || {}));
    if (editId) {
      // Modo edición: cargar el perfil específico
      api.get(`/profiles/${editId}`).then(({ data }) => {
        if (data) {
          setExistingId(data.id);
          setForm({
            name: data.name || "",
            type: data.type || "canguro",
            bio: data.bio || "",
            price_per_hour: data.price_per_hour || 12,
            photo_url: data.photo_url || "",
            comunidad: data.comunidad || "",
            ciudad: data.ciudad || "",
            zonas: data.zonas || [],
            availability: data.availability || [],
            languages: data.languages || [],
            subjects: data.subjects || {},
            accepts_privacy_policy: data.accepts_privacy_policy || false,
            accepts_communications: data.accepts_communications || false,
          });
        }
      }).catch(() => {});
    }
  }, [editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArr = (key, val) => setForm((f) => {
    const arr = new Set(f[key] || []);
    arr.has(val) ? arr.delete(val) : arr.add(val);
    return { ...f, [key]: Array.from(arr) };
  });

  const toggleSubject = (cat, item) => setForm((f) => {
    const cur = { ...(f.subjects || {}) };
    const list = new Set(cur[cat] || []);
    list.has(item) ? list.delete(item) : list.add(item);
    if (list.size === 0) delete cur[cat]; else cur[cat] = Array.from(list);
    return { ...f, subjects: cur };
  });

  const ciudades = form.comunidad ? Object.keys(locations[form.comunidad] || {}) : [];
  const zonasOpts = form.comunidad && form.ciudad ? (locations[form.comunidad]?.[form.ciudad] || []) : [];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.photo_url) {
      setError("Debes subir una foto de perfil");
      return;
    }
    if (!form.accepts_privacy_policy) {
      setError("Debes aceptar la Política de Privacidad para continuar");
      return;
    }
    if (!form.accepts_communications) {
      setError("Debes aceptar recibir comunicaciones relacionadas con tu actividad");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, price_per_hour: Number(form.price_per_hour) };
      if (existingId) {
        await api.put(`/profiles/${existingId}`, payload);
      } else {
        await api.post("/profiles", payload);
      }
      navigate("/profile");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    setError("");
    setUploading(true);
    try {
      // Comprime automáticamente hasta ≤ 2 MB (re-encodea a JPEG)
      const file = await compressImageToMaxSize(original, { maxBytes: 2 * 1024 * 1024 });
      if (file.size > 2 * 1024 * 1024) {
        setError("No se pudo reducir la imagen por debajo de 2 MB. Prueba con otra foto.");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("photo_url", data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in-up max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/profile")} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="back-btn">
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-extrabold">
          {existingId ? "Editar mi perfil" : "Crear mi perfil profesional"}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Tipo */}
        <div className="kibli-card p-5">
          <label className="block text-sm font-semibold mb-2">Soy...</label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => update("type", "canguro")} data-testid="type-canguro-btn"
              className={`px-4 py-3 rounded-xl border-2 font-semibold ${form.type === "canguro" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200"}`}>
              <i className="fa-solid fa-heart mr-2"></i>Canguro
            </button>
            <button type="button" onClick={() => update("type", "profesor")} data-testid="type-profesor-btn"
              className={`px-4 py-3 rounded-xl border-2 font-semibold ${form.type === "profesor" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200"}`}>
              <i className="fa-solid fa-graduation-cap mr-2"></i>Profesor
            </button>
          </div>
        </div>

        {/* Info básica */}
        <div className="kibli-card p-5 space-y-4">
          <Field label="Foto de perfil (obligatoria)">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center flex-shrink-0">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Foto" className="w-full h-full object-cover" data-testid="cp-photo-preview" />
                ) : (
                  <i className="fa-solid fa-camera text-slate-400 text-2xl"></i>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="cp-photo-input"
                />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="btn-primary px-4 py-2 text-sm" data-testid="cp-photo-upload-btn">
                  <i className="fa-solid fa-upload mr-1"></i>{uploading ? "Subiendo..." : form.photo_url ? "Cambiar foto" : "Subir foto"}
                </button>
                <p className="text-xs text-slate-500 mt-1">JPG, PNG, WEBP o GIF. Máx. 2 MB.</p>
              </div>
            </div>
          </Field>
          <Field label="Nombre público">
            <input className="input-base" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="cp-name" />
          </Field>
          <Field label="Sobre mí">
            <textarea rows={3} className="input-base" value={form.bio} onChange={(e) => update("bio", e.target.value)} data-testid="cp-bio" />
          </Field>
          <Field label={`Precio por hora (${form.price_per_hour}€)`}>
            <input type="range" min="5" max="50" value={form.price_per_hour} onChange={(e) => update("price_per_hour", e.target.value)} className="w-full kibli-slider" data-testid="cp-price" />
          </Field>
        </div>

        {/* Ubicación */}
        <div className="kibli-card p-5 space-y-3">
          <div className="font-bold">📍 Ubicación</div>
          <Field label="Comunidad Autónoma">
            <select className="input-base" value={form.comunidad} onChange={(e) => update("comunidad", e.target.value)} required data-testid="cp-comunidad">
              <option value="">Selecciona</option>
              {Object.keys(locations).sort().map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Ciudad">
            <select className="input-base" value={form.ciudad} onChange={(e) => update("ciudad", e.target.value)} required disabled={!form.comunidad} data-testid="cp-ciudad">
              <option value="">Selecciona</option>
              {ciudades.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          {form.ciudad && (
            <Field label="Zonas / Barrios">
              <div className="flex flex-wrap gap-2">
                {zonasOpts.map((z) => (
                  <button key={z} type="button" onClick={() => toggleArr("zonas", z)} data-testid={`cp-zona-${z}`}
                    className={`kibli-pill ${form.zonas.includes(z) ? "kibli-pill-active" : ""}`}>{z}</button>
                ))}
              </div>
            </Field>
          )}
        </div>

        {/* Disponibilidad */}
        <div className="kibli-card p-5">
          <div className="font-bold mb-3">Disponibilidad</div>
          <div className="flex flex-wrap gap-2">
            {AVAILABILITY_OPTIONS.map((o) => (
              <button key={o.value} type="button" onClick={() => toggleArr("availability", o.value)} data-testid={`cp-avail-${o.value}`}
                className={`kibli-pill ${form.availability.includes(o.value) ? "kibli-pill-active" : ""}`}>{o.label}</button>
            ))}
          </div>
        </div>

        {/* Idiomas */}
        <div className="kibli-card p-5">
          <div className="font-bold mb-3">Idiomas</div>
          <div className="flex flex-wrap gap-2">
            {LANGUAGE_OPTIONS.map((l) => (
              <button key={l} type="button" onClick={() => toggleArr("languages", l)} data-testid={`cp-lang-${l}`}
                className={`kibli-pill ${form.languages.includes(l) ? "kibli-pill-active" : ""}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Asignaturas (only for profesores) */}
        {form.type === "profesor" && (
          <div className="kibli-card p-5">
            <div className="font-bold mb-3">Asignaturas</div>
            <div className="space-y-3">
              {Object.entries(SUBJECTS_TREE).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-sm font-semibold mb-1 text-slate-700">{cat}</div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((s) => (
                      <button key={s} type="button" onClick={() => toggleSubject(cat, s)} data-testid={`cp-subj-${cat}-${s}`}
                        className={`kibli-pill text-xs ${(form.subjects[cat] || []).includes(s) ? "kibli-pill-active-teal" : ""}`}>{s}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="cp-error">{error}</div>}

        {/* Protección de datos */}
        <div className="kibli-card p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="cp-privacy-label">
            <input
              type="checkbox"
              checked={form.accepts_privacy_policy}
              onChange={(e) => update("accepts_privacy_policy", e.target.checked)}
              className="mt-1 w-5 h-5 accent-blue-600 flex-shrink-0"
              data-testid="cp-privacy-checkbox"
              required
            />
            <div className="text-sm leading-relaxed">
              <div className="font-bold text-slate-900 mb-2">Acepto la Política de Privacidad</div>
              <p className="text-slate-600">
                Al crear una cuenta, aceptas nuestra Política de Privacidad y consientes el tratamiento de tus datos personales conforme a lo siguiente:
              </p>
              <ul className="mt-2 space-y-1 text-slate-600 list-none">
                <li>• <span className="font-semibold">Responsable del tratamiento:</span> KibliApp</li>
                <li>• <span className="font-semibold">Finalidad:</span> Gestionar tu cuenta, ponerte en contacto con familias, facilitar servicios de cuidado/enseñanza y mejorar la plataforma.</li>
                <li>• <span className="font-semibold">Datos tratados:</span> Nombre, contacto, ubicación, experiencia, disponibilidad y otros datos que proporciones.</li>
                <li>• <span className="font-semibold">Legitimación:</span> Tu consentimiento y la ejecución del contrato de servicios.</li>
                <li>• <span className="font-semibold">Destinatarios:</span> Usuarios de la app (familias) y proveedores técnicos necesarios.</li>
                <li>• <span className="font-semibold">Derechos:</span> Puedes acceder, rectificar o eliminar tus datos, así como otros derechos, escribiendo a <a className="text-blue-600 underline" href="mailto:soporte@kibliapp.com">soporte@kibliapp.com</a></li>
              </ul>
            </div>
          </label>

          <div className="border-t border-slate-100"></div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="cp-comms-label">
            <input
              type="checkbox"
              checked={form.accepts_communications}
              onChange={(e) => update("accepts_communications", e.target.checked)}
              className="mt-1 w-5 h-5 accent-blue-600 flex-shrink-0"
              data-testid="cp-comms-checkbox"
              required
            />
            <div className="text-sm leading-relaxed">
              <div className="font-bold text-slate-900">
                Acepto recibir comunicaciones relacionadas con mi actividad en la app (mensajes de familias, notificaciones del servicio, recordatorios).
              </div>
            </div>
          </label>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full py-4" data-testid="cp-submit-btn">
          {loading ? "Guardando..." : existingId ? "Guardar cambios" : "Crear perfil"}
        </button>

        {!existingId && (
          <p className="text-center text-sm text-slate-500">
            Tu perfil quedará pendiente de revisión por un administrador antes de ser visible.
          </p>
        )}
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
